import { LoaderFunctionArgs, useLoaderData } from "react-router"
import { DocsRouterLink as RouterLink } from "~/docs-data/docs-link"
import { H1, P, Section } from "~/components/Typography"
import { LocationTypeBadge } from "~/components/LocationTypeBadge"
import { getCountryCatalog } from "~/docs-data/helpers"
import { locationSets } from "~/docs-data/concepts"
import { getDocsCatalogForRequest } from "~/server/docs-catalog-store.server"
import { getLocationSetCounts } from "~/server/location-set-counts.server"

// 32963 → "33K", 6123456 → "6.1M".
const formatLocationCount = (n: number): string =>
  new Intl.NumberFormat("en-US", {
    notation: "compact",
    maximumFractionDigits: 1,
  }).format(n)

export const loader = async ({ request, params }: LoaderFunctionArgs) => {
  const { countries } = await getDocsCatalogForRequest(request)
  const cc = getCountryCatalog(countries, params.country ?? "")
  if (!cc) throw new Response("Not found", { status: 404 })
  const counts = getLocationSetCounts()
  const setsForCountry = locationSets
    .filter((ls) => ls.country === cc.countryCode)
    .map((ls) => ({ ...ls, locationCount: counts[ls.id] ?? null }))
  return { cc, setsForCountry }
}

export default function CountryLocationSets() {
  const { cc, setsForCountry } = useLoaderData<typeof loader>()

  return (
    <div className="max-w-screen-xl">
      <Section>
        <H1>Location sets</H1>
        <P>
          A location set is a concrete collection of locations available in {cc.name}: the actual
          places, areas, or ways that metrics are computed against. Each dataset is anchored to one
          location set.
        </P>
      </Section>

      <Section>
        {setsForCountry.length === 0 ? (
          <div className="border rounded-md py-12 text-center text-muted-foreground">
            No location sets recorded for {cc.name} yet.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {setsForCountry.map((ls) => (
              <RouterLink
                key={ls.id}
                to={`/docs/datasets/${cc.code}/location-sets/${ls.id}`}
                className="group block rounded-lg border bg-card hover:border-primary hover:shadow-sm transition-all p-5"
              >
                <div className="flex items-start justify-between gap-2 mb-2">
                  <LocationTypeBadge type={ls.locationType} className="text-xs" />
                  {/* Location count hidden for now.
                  {ls.locationCount != null && (
                    <span className="text-xs text-muted-foreground font-mono">
                      ~{formatLocationCount(ls.locationCount)}
                    </span>
                  )}
                  */}
                </div>
                <h3 className="font-semibold text-sm group-hover:text-primary transition-colors mb-1.5">
                  {ls.shortName}
                </h3>
                <p className="text-xs text-muted-foreground line-clamp-3">
                  {ls.description}
                </p>
              </RouterLink>
            ))}
          </div>
        )}
      </Section>
    </div>
  )
}
